import { apiGet, apiPost } from '@/utils/request';

export interface ToutiaoAuthListParams extends Record<string, unknown> {
  page?: number;
  page_size?: number;
  id?: string | number;
  name?: string;
  status?: 0 | 1;
}

/** 授权下的工作台子账户；上报开关已下沉到「app × 子账号」维度 */
export interface ToutiaoSubAccountItem {
  /** 工作台子账户ID（开关接口用） */
  account_id: string;
  account_name: string;
  /** 账户角色枚举值，如 PLATFORM_ROLE_LIFE */
  account_role: string;
  /** 角色中文名，如「抖音来客账户」 */
  role_name: string;
  /** 是否有效 0=无效 1=有效 */
  is_valid: 0 | 1;
  /** 该子账号下的广告户ID，逗号分隔 */
  advertiser_ids: string;
  /** 子账号上报开关真实值 0=关 1=开 */
  report_status: 0 | 1;
}

export interface ToutiaoAuthItem {
  id: number;
  auth_user_id: string;
  display_name: string;
  email: string;
  advertiser_ids: string;
  account_ids: string;
  /** 只读聚合值：任一子账号开启=1，仅供展示，不可编辑 */
  report_status: 0 | 1;
  show_advertiser_ids: string;
  show_local_adv_ids: string;
  /** 该授权下全部子账号 */
  sub_accounts: ToutiaoSubAccountItem[];
  created_at: string;
  updated_at: string;
}

export interface ToutiaoAuthListData {
  total: number;
  page: number;
  page_size: number;
  list: ToutiaoAuthItem[];
}

/** 编辑授权配置；上报开关请走 setSubAccountReportStatus，此处传 report_status 会被后端忽略 */
export interface ToutiaoEditParams {
  id: number;
  clue_sync_status?: 0 | 1;
  advertiser_ids?: string;
  local_adv_ids?: string;
}

export interface ToutiaoAdvertiserItem {
  advertiser_id: string;
  advertiser_name: string;
  type: 'AD' | 'LOCAL' | 'ENTERPRISE';
  selected: boolean;
}

export interface ToutiaoAdvertisersData {
  auth: { id: number; auth_user_id: string; display_name: string };
  list: ToutiaoAdvertiserItem[];
}

/** 同步子账号结果（三向同步：新增落库、已有更新、平台已解绑的删除） */
export interface ToutiaoSyncSubAccountsData {
  /** 本次新增的子账户ID */
  added: string[];
  /** 本次移除的子账户ID（平台已解绑） */
  removed: string[];
  /** 已存在并刷新信息的子账户数量 */
  updated: number;
  /** 同步后父账号的子账户ID串（逗号分隔） */
  account_ids: string;
  /** 同步后该授权下广告户总数 */
  advertiser_count: number;
}

export const toutiaoAdApi = {
  getAuthList: (params: ToutiaoAuthListParams) =>
    apiGet<ToutiaoAuthListData>('/toutiao-ad/auth-list', params),

  getAuthUrl: () => apiGet<{ url: string }>('/toutiao-ad/auth-url'),

  edit: (params: ToutiaoEditParams) => apiPost<null>('/toutiao-ad/edit', params),

  getAdvertisers: (params: { auth_id: number; type?: 'AD' | 'LOCAL' | 'ENTERPRISE' }) =>
    apiGet<ToutiaoAdvertisersData>('/toutiao-ad/advertisers', params),

  /** 用存量 access_token 重新拉取该授权下的子账户与广告户，与本地数据三向同步 */
  syncSubAccounts: (params: { auth_id: number }) =>
    apiPost<ToutiaoSyncSubAccountsData>('/toutiao-ad/sync-sub-accounts', params),

  /** 设置子账号上报开关（开关维度为「app × 子账号」） */
  setSubAccountReportStatus: (params: {
    auth_id: number;
    account_id: string;
    report_status: 0 | 1;
  }) =>
    apiPost<{ account_id: string; report_status: 0 | 1 }>(
      '/toutiao-ad/sub-account/report-status',
      params,
    ),
};
